import React, { useEffect, useRef, useState } from 'react';
import './ShootingStarCursor.css';

const ShootingStarCursor = () => {
  const cursorRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  
  const mousePosition = useRef({ x: 0, y: 0 });
  const cursorPosition = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const cursor = cursorRef.current;
    let animationId;

    const updateCursor = () => {
      const dx = mousePosition.current.x - cursorPosition.current.x;
      const dy = mousePosition.current.y - cursorPosition.current.y;
      
      // Increased smoothing factor from 0.15 to 0.25 for faster response
      cursorPosition.current.x += dx * 0.25;
      cursorPosition.current.y += dy * 0.25;

      if (cursor) {
        cursor.style.transform = `translate(${cursorPosition.current.x - 10}px, ${cursorPosition.current.y - 10}px)`;
      }

      animationId = requestAnimationFrame(updateCursor);
    };

    const handleMouseMove = (e) => {
      mousePosition.current.x = e.clientX;
      mousePosition.current.y = e.clientY;
      
      // Reduced trail frequency for better performance
      if (Math.random() > 0.7) {
        createShootingStar(e.clientX, e.clientY);
      }
    };

    const handleMouseEnter = (e) => {
      try {
        const target = e.target;
        if (
          target.matches &&
          target.matches(
            'a, button, [role="button"], input, textarea, select, .cta-primary, .cta-secondary, .nav-link, .logo, .testimonial-card'
          )
        ) {
          setIsHovering(true);
        }
      } catch (err) {
        console.warn('MouseEnter error:', err);
      }
    };

    const handleMouseLeave = (e) => {
      try {
        const target = e.target;
        if (
          target.matches &&
          target.matches(
            'a, button, [role="button"], input, textarea, select, .cta-primary, .cta-secondary, .nav-link, .logo, .testimonial-card'
          )
        ) {
          setIsHovering(false);
        }
      } catch (err) {
        console.warn('MouseLeave error:', err);
      }
    };

    const handleMouseDown = () => {
      setIsClicking(true);
      createStarBurst(mousePosition.current.x, mousePosition.current.y);
    };

    const handleMouseUp = () => {
      setIsClicking(false);
    };

    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseenter', handleMouseEnter, true);
    document.addEventListener('mouseleave', handleMouseLeave, true);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    updateCursor();

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseenter', handleMouseEnter, true);
      document.removeEventListener('mouseleave', handleMouseLeave, true);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  const createShootingStar = (x, y) => {
    const star = document.createElement('div');
    star.className = 'shooting-star';
    
    const colors = ['#FFD700', '#00D9FF', '#9333EA', '#FF6B35'];
    const color = colors[Math.floor(Math.random() * colors.length)];
    
    star.style.cssText = `
      position: fixed;
      left: ${x}px;
      top: ${y}px;
      color: ${color};
      z-index: 9998;
      pointer-events: none;
    `;
    
    document.body.appendChild(star);
    
    // Faster, more direct animation
    star.style.opacity = '1';
    star.style.animation = 'shootingStar 0.6s ease-out forwards';
    
    setTimeout(() => {
      if (star.parentNode) {
        star.parentNode.removeChild(star);
      }
    }, 600);
  };

  const createStarBurst = (x, y) => {
    for (let i = 0; i < 6; i++) {
      setTimeout(() => {
        const star = document.createElement('div');
        star.className = 'shooting-star';
        
        const angle = (i / 6) * Math.PI * 2;
        const distance = 40 + Math.random() * 20;
        const dx = Math.cos(angle) * distance;
        const dy = Math.sin(angle) * distance;
        
        const colors = ['#FFD700', '#00D9FF', '#9333EA', '#FF6B35'];
        const color = colors[Math.floor(Math.random() * colors.length)];
        
        star.style.cssText = `
          position: fixed;
          left: ${x}px;
          top: ${y}px;
          color: ${color};
          z-index: 9998;
          pointer-events: none;
          --dx: ${dx}px;
          --dy: ${dy}px;
        `;
        
        document.body.appendChild(star);
        
        star.style.opacity = '1';
        star.style.animation = 'starBurst 0.8s ease-out forwards';
        
        setTimeout(() => {
          if (star.parentNode) {
            star.parentNode.removeChild(star);
          }
        }, 800);
      }, i * 30);
    }
  };

  return (
    <div 
      ref={cursorRef} 
      className={`shooting-star-cursor ${isHovering ? 'hover' : ''} ${isClicking ? 'click' : ''}`}
    >
      <div className="cursor-trail"></div>
      <div className="cursor-core"></div>
      <div className="cursor-ring"></div>
    </div>
  );
};

export default ShootingStarCursor;